import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  User, Smartphone, Save, Heart, Users, Eye,
  MessageCircle, Clock, Settings, Video, Shield
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const ProfileCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 24px; display: flex; align-items: center; gap: 20px;
`;

const Avatar = styled.div`
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #ff0050, #00f2ea);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 24px; font-weight: 700; flex-shrink: 0;
`;

const ProfileMeta = styled.div`
  flex: 1;
  h2 { font-size: 18px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px; }
`;

const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const StatCard = styled.div`
  padding: 14px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border); text-align: center;
`;
const StatVal = styled.div`font-size: 20px; font-weight: 800; color: var(--text-primary);`;
const StatLabel = styled.div`font-size: 9px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; margin-top: 2px;`;
const StatIcon = styled.div`display: flex; justify-content: center; margin-bottom: 4px; color: ${p => p.$color || 'var(--primary)'};`;

const Section = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 12px; padding: 18px 20px;
`;
const SectionTitle = styled.h3`
  font-size: 14px; font-weight: 700; color: var(--text-dark);
  display: flex; align-items: center; gap: 8px; margin: 0 0 14px;
  svg { color: var(--primary); }
`;

const FieldGroup = styled.div`margin-bottom: 12px; &:last-child { margin-bottom: 0; }`;
const FieldLabel = styled.label`
  display: block; font-size: 10px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .3px; margin-bottom: 4px;
`;
const FieldInput = styled.input`
  width: 100%; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px;
  &:focus { border-color: var(--primary); outline: none; }
`;
const FieldSelect = styled.select`
  width: 100%; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px;
  &:focus { border-color: var(--primary); outline: none; }
`;

const ParamGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const SaveBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 10px 20px;
  border-radius: 10px; border: none; font-weight: 700; font-size: 12px;
  background: linear-gradient(135deg, #ff0050, #00f2ea); color: #fff; cursor: pointer;
  &:hover { transform: translateY(-1px); box-shadow: var(--shadow-hover); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const MetaTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: var(--text-muted); font-weight: 500;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 50vh; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const TikTokAccount = ({ selectedDevice }) => {
  const [profile, setProfile] = useState(null);
  const [, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    tiktok_username: '',
    tiktok_password: '',
    account_type: 'OUR',
    niche: '',
    bio: '',
    daily_like_limit: '200',
    daily_follow_limit: '50',
    daily_comment_limit: '20',
    session_length_mins: '60',
    like_delay: '3-8',
    follow_delay: '5-12',
  });

  const fetchProfile = async () => {
    if (!selectedDevice?.device_id) return;
    setLoading(true);
    try {
      const r = await axios.get(`/api/tiktok-scripts/account/${selectedDevice.device_id}`);
      if (r.data.success && r.data.account) {
        const a = r.data.account;
        setProfile(a);
        setForm(prev => ({ ...prev, ...a }));
      }
    } catch { /* no account yet */ }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProfile(); }, [selectedDevice?.device_id]);

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const saveProfile = async () => {
    if (!selectedDevice?.device_id) return;
    setSaving(true);
    try {
      await axios.put(`/api/tiktok-scripts/account/${selectedDevice.device_id}`, form);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  if (!selectedDevice) {
    return <Page><EmptyState><Smartphone size={48} /><p>Sélectionnez un appareil</p></EmptyState></Page>;
  }

  return (
    <Page>
      <Header>
        <Title><User size={22} style={{ color: '#ff0050' }} /> TikTok — Compte</Title>
        <SaveBtn onClick={saveProfile} disabled={saving}>
          <Save size={14} /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </SaveBtn>
      </Header>

      <ProfileCard>
        <Avatar>{form.tiktok_username ? form.tiktok_username[0].toUpperCase() : '?'}</Avatar>
        <ProfileMeta>
          <h2>@{form.tiktok_username || '—'}</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <MetaTag><Smartphone size={11} /> {selectedDevice.device_name || selectedDevice.device_id}</MetaTag>
            <MetaTag><Shield size={11} /> {form.account_type || 'OUR'}</MetaTag>
            {form.niche && <MetaTag><Video size={11} /> {form.niche}</MetaTag>}
          </div>
        </ProfileMeta>
      </ProfileCard>

      <StatsRow>
        <StatCard><StatIcon $color="#ff0050"><Heart size={16} /></StatIcon><StatVal>{profile?.total_likes || 0}</StatVal><StatLabel>Likes donnés</StatLabel></StatCard>
        <StatCard><StatIcon $color="#00f2ea"><Users size={16} /></StatIcon><StatVal>{profile?.total_follows || 0}</StatVal><StatLabel>Follows</StatLabel></StatCard>
        <StatCard><StatIcon $color="#25f4ee"><MessageCircle size={16} /></StatIcon><StatVal>{profile?.total_comments || 0}</StatVal><StatLabel>Commentaires</StatLabel></StatCard>
        <StatCard><StatIcon $color="#fe2c55"><Eye size={16} /></StatIcon><StatVal>{profile?.total_views || 0}</StatVal><StatLabel>Vues</StatLabel></StatCard>
        <StatCard><StatIcon><Clock size={16} /></StatIcon><StatVal>{profile?.total_sessions || 0}</StatVal><StatLabel>Sessions</StatLabel></StatCard>
      </StatsRow>

      <Section>
        <SectionTitle><User size={16} /> Identifiants TikTok</SectionTitle>
        <ParamGrid>
          <FieldGroup>
            <FieldLabel>Nom d'utilisateur</FieldLabel>
            <FieldInput value={form.tiktok_username} onChange={e => updateField('tiktok_username', e.target.value)} placeholder="@username" />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Mot de passe</FieldLabel>
            <FieldInput type="password" value={form.tiktok_password} onChange={e => updateField('tiktok_password', e.target.value)} placeholder="••••••" />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Type de compte</FieldLabel>
            <FieldSelect value={form.account_type} onChange={e => updateField('account_type', e.target.value)}>
              <option value="OUR">Notre compte</option>
              <option value="CLIENT">Client</option>
            </FieldSelect>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Niche / Thématique</FieldLabel>
            <FieldInput value={form.niche} onChange={e => updateField('niche', e.target.value)} placeholder="ex: fitness, cuisine, tech..." />
          </FieldGroup>
        </ParamGrid>
      </Section>

      <Section>
        <SectionTitle><Settings size={16} /> Paramètres d'automatisation</SectionTitle>
        <ParamGrid>
          <FieldGroup>
            <FieldLabel>Limite likes / jour</FieldLabel>
            <FieldInput type="number" value={form.daily_like_limit} onChange={e => updateField('daily_like_limit', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Limite follows / jour</FieldLabel>
            <FieldInput type="number" value={form.daily_follow_limit} onChange={e => updateField('daily_follow_limit', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Limite commentaires / jour</FieldLabel>
            <FieldInput type="number" value={form.daily_comment_limit} onChange={e => updateField('daily_comment_limit', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Durée session max (min)</FieldLabel>
            <FieldInput type="number" value={form.session_length_mins} onChange={e => updateField('session_length_mins', e.target.value)} />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Délai entre likes (sec)</FieldLabel>
            <FieldInput value={form.like_delay} onChange={e => updateField('like_delay', e.target.value)} placeholder="3-8" />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>Délai entre follows (sec)</FieldLabel>
            <FieldInput value={form.follow_delay} onChange={e => updateField('follow_delay', e.target.value)} placeholder="5-12" />
          </FieldGroup>
        </ParamGrid>
      </Section>
    </Page>
  );
};

export default TikTokAccount;
